(ns frontend.view.settings2.view
  (:require
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.subscription :as subscription]
   [common.modules :as modules]
   [frontend.actions.tenant :as tenant-actions]
   [frontend.logging :as logging]
   [frontend.view.settings2.account-information.view :as account-info]
   [frontend.view.settings2.authorized-applications.view :as authorized-applications]
   [frontend.view.settings2.db :as db]
   [frontend.view.settings2.event :as event]
   [frontend.view.settings2.module-management.view :as module-management]
   [frontend.view.settings2.security.view :as security]
   [frontend.view.settings2.security.ai-admin.view :as ai-admin]
   [frontend.view.settings2.security.mcp-admin.view :as mcp-admin]
   [frontend.view.settings2.state :as state]
   [frontend.view.user-management.db :as um-db]
   [frontend.view.user-management.guest :as um-guest]
   [frontend.view.user-management.insider-groups :as um-insider]
   [frontend.view.user-management.teams :as um-teams]
   [frontend.view.user-management.users :as um-users]
   [frontend.view.user-management.view :as um-view]
   [ui.components.alert.view :as alert]
   [potpuri.core :refer [index-by]]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.link.view :as link]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.dom-globals :as dg]))

(defn card-body
  ([routing-path title desc]
   (card-body routing-path title desc false))
  ([routing-path title desc feature-disabled?]
   [tooltip/as-child
    {:disabled? (not feature-disabled?)}
    (fn [f-props]
      [:div
       (merge (dissoc f-props :onClick :className)                                                  ;;remove conflicting Radix props
              {:id       (str "settings-" title)
               :class    "px-4 py-3
                         border border-solid border-base-light-border-paler
                         bg-base-light hover:bg-base-regular
                         rounded cursor-pointer"
               :on-click (fn [e]
                           (when-not feature-disabled?
                             (routing/set-view! [routing-path])
                             (dg/prevent-and-stop e)
                             false))})
       [:p {:class (str "font-bold text-base-light-text "
                        (when feature-disabled? "text-disabled-regular-text "))}
        (loc/tr title)]
       [:span
        {:class (str "pt-1 mb-0 text-sm text-base-light-text-paler line-clamp-3 "
                     (when feature-disabled? "text-disabled-light-text"))}
        (loc/tr desc)]])
    (loc/tr :feature-not-enabled)]))

(defn toolbar-buttons-for-tab
  [tab-key]
  (cond (= tab-key state/users-route)          (um-view/invite-user-button)
        (= tab-key state/teams-route)          (um-view/create-team-button)
        (= tab-key state/insider-groups-route) (um-view/create-insider-group-button)
        :else                                  []))

(defn visible-tabs
  []
  (let [guests-visible? (or (session/has-feature-access? :feature/sell-side-guest)
                            (session/has-feature-access? :feature/buy-side-guest))
        insider-groups-visible? (session/has-feature-access? :feature/insider-groups)
        zapai-visible? (and (session/is-admin?)
                            (session/has-feature-access? :feature/ai-deal-parsing))
        mcp-admin-visible? (and (session/is-admin?)
                                (session/has-feature-access? :feature/mcp-connector))]
    (->> state/tabs
         (remove (fn [{:keys [key]}]
                   (or (and (= key :guest-users) (not guests-visible?))
                       (and (= key :insider-groups) (not insider-groups-visible?))
                       (and (= key :zapai) (not zapai-visible?))
                       (and (= key :mcp-admin) (not mcp-admin-visible?))))))))

(defn users-card
  []
  [um-users/view um-db/state])

(defn teams-card
  []
  [um-teams/view um-db/state])

(defn insider-groups-card
  []
  [um-insider/view um-db/state])

(defn guest-users-card
  []
  (if (or (session/has-feature-access? :feature/sell-side-guest)
          (session/has-feature-access? :feature/buy-side-guest))
    [um-guest/view um-db/state]
    [um-guest/advertisement]))

(defn security-card
  []
  (let [tenant-id (:db/id @session/tenant)
        tenant-cursor (r/cursor session/entities [tenant-id])]
    [:div
     {:class "flex flex-col gap-8"}
     [security/security-view tenant-cursor]
     [:div
      {:class "flex flex-col gap-4"}
      [:span
       {:class "text-xl font-bold"}
       (loc/tr :settings2/title-authorized-applications)]
      [authorized-applications/content]]]))

(defn zapai-card
  []
  [ai-admin/view])

(defn mcp-admin-card
  []
  [mcp-admin/view])

(defn configuration-card
  [fundraising-plan?]
  [:div
   {:class "mb-4 grid grid-cols-1 lg:grid-cols-2 gap-3"}
   [card-body :settings.permissions/fields :settings.configuration/fields
    :settings2/desc-fields]
   (when-not fundraising-plan?
     [card-body :settings/funds :settings.configuration/funds :settings2/desc-funds
      (not (session/has-feature-access? :feature/fund-management))])
   (when-not fundraising-plan?
     [card-body :settings/deal-flow-pipelines :settings.configuration/deal-pipelines
      :settings2/desc-deal-pipelines (not (session/dealflow?))])
   [card-body :settings/fundraising-pipelines
    :settings.configuration/fundraising-pipelines :settings2/desc-fundraising-pipelines
    (not (session/fundraising?))]
   (when-not fundraising-plan?
     [card-body :settings/sales-pipelines :settings.configuration/sales-pipelines
      :settings2/desc-sales-pipelines
      (not (session/has-feature-access? :feature/sales-pipelines))])
   (when-not fundraising-plan?
     [card-body :settings/exit-pipelines :settings.configuration/exit-pipelines
      :settings2/desc-exit-pipelines
      (not (session/has-feature-access? :feature/exit-process-management))])
   #_[card-body :new-settings :settings2/title-target-company-reporting
      :settings2/desc-target-company-reporting]
   (when-not fundraising-plan?
     [card-body :new-settings-web-form :settings.configuration/web-forms
      :settings2/desc-web-form (not (session/dealflow?))])

   (when (and (not fundraising-plan?)
              (session/is-admin?)
              (session/automated-kyc-renewal?))
     [card-body :settings/kyc :settings.configuration/kyc :settings2/desc-kyc])

   (when (and (not fundraising-plan?)
              (session/is-admin?)
              (session/has-feature-access? :feature/counterparty-diligence))
     [card-body :settings/counterparty-diligence
      :settings.configuration/counterparty-diligence
      :settings2/desc-counterparty-diligence])

   (when-not fundraising-plan?
     [card-body :settings/screening :settings.configuration/screening
      :settings2/desc-screening (not (session/screening?))])
   (when-not fundraising-plan?
     [card-body :settings/surveys :settings.configuration/surveys :settings2/desc-surveys
      (not (session/has-feature-access? :feature/surveys))])
   [card-body :settings/reporting-db :settings.configuration/reporting-db :settings2/desc-reporting-db
    (not (session/has-feature-access? :feature/reporting-db))]
   (when (session/is-admin?)
     [card-body :settings/mcp :settings.configuration/mcp :settings2/desc-mcp
      (not (session/has-feature-access? :feature/mcp-connector))])
   [card-body :settings/e-signature :settings.configuration/e-signature
    :settings2/desc-e-signature (not (session/has-feature-access? :feature/entity-esign))]
   [card-body :settings/metabase-dashboards :settings.configuration/metabase-dashboards
    :settings2/desc-metabase-dashboards (not (session/metabase-dashboards?))]
   [card-body :settings/decisions :settings.configuration/decisions
    :settings2/desc-decisions (not (session/decisions?))]
   [card-body :settings/contact-lists :settings.configuration/contact-lists
    :settings2/desc-contact-lists (not (session/contact-lists?))]
   (when (session/zap-vault?)
     [card-body :settings/zap-vaults :settings.configuration/zap-vaults :settings2/desc-zap-vaults])])

(defn your-modules-card
  []
  (let [attribute :tenant/modules
        tenant-id (:db/id @session/tenant)
        entity-atom (r/cursor session/entities [tenant-id])
        all-modules (->> (modules/all-modules @session/tenant @session/tenant-plan)
                         (remove (fn [module]
                                   (= :module/dashboards-admin module)))
                         (sort))
        module (set (get @entity-atom attribute))]
    [:div
     {:class "mb-4 flex flex-col gap-4"}
     [alert/info
      {}
      [:span (loc/tr :settings2/your-modules-banner)]]
     [module-management/module-selector
      {:value     module
       :values    all-modules
       :edit?     true
       :on-change (fn [v]
                    (let [updated-modules (if (contains? module v)
                                            (disj module v)
                                            (conj module v))]
                      (swap! entity-atom assoc attribute updated-modules)
                      (tenant-actions/*->update!
                       tenant-id
                       attribute
                       updated-modules
                       (fn [_])
                       (fn [e]
                         (logging/error "Unable to update modules" e)
                         (when (= (get @entity-atom attribute) updated-modules)
                           (swap! entity-atom assoc attribute module))))))}]]))

(defn your-business-card
  []
  [:div
   {:class "mb-4 grid grid-cols-1 gap-3"}
   #_[card-body :new-settings/account-information :settings2/title-account-information
      :settings2/desc-account-information]
   [account-info/component]
   #_[card-body :new-settings :settings2/title-branding :settings2/desc-branding]
   #_[card-body :new-settings :settings2/title-payments-invoices
      :settings2/desc-payments-invoices]])

(defn your-data-card
  []
  [:div
   {:class "mb-4 grid grid-cols-1 gap-3"}
   [card-body :import-data :settings2/title-import-data-from-excel
    :settings2/desc-import-data-from-excel]
   [card-body :new-settings/api-integrations :settings2/title-api-integrations
    :settings2/desc-api-integrations]
   [card-body :new-settings/export-data :settings2/title-export-data-to-excel
    :settings2/desc-export-data-to-excel]])

(defn component
  [_tab-key]
  (r/create-class
   {:component-did-mount
    (fn [_]
      (swap! db/state assoc-in [:new-tenant :tenant/default-currency] :eur)                         ; TODO why we have it hardcoded here?
      (when-not (:entities db/state)
        (subscription/open ::settings
                           :settings
                           {}
                           (fn [{:keys [entities]}]
                             (->> entities
                                  (cu/only-entity :attachment/type :attachment.type/export)
                                  (index-by :db/id)
                                  (swap! db/state update :exports merge))
                             ;; @VATAJA check this out
                             ; TODO we can not use manage-entities here as is,
                             ; TODO we need to show disabled entities in settings view
                             ; TODO refactor manage-entities and uncomment it
                             ; (manage-entities session/reverse-lookup db/state entities)
                             (->> entities
                                  (index-by :db/id)
                                  (swap! db/state update :entities merge))
                             :ok))))
    :component-will-unmount
    (fn [_]
      (subscription/close ::settings)
      (swap! db/state dissoc :entities))
    :reagent-render
    (fn [tab-key]
      (let [fundraising-plan? (= :plan/fundraising
                                 (->> @session/tenant
                                      :tenant/product-plan
                                      :plan/type))]
        [:<>
         [zap-toolbar/view db/state (partial zap-toolbar/back-button nil) nil (toolbar-buttons-for-tab tab-key)]
         [:div
          {:class "pb-8
                  text-base-light-text
                  bg-base-light
                  h-[calc(100vh-52px-37px)]
                  overflow-y-auto
                  [scrollbar-gutter:stable]"}
          [:div
           {:class "max-w-7xl 2xl:max-w-screen-2xl m-auto px-4 py-6"}

           [:div
            {:class "grid grid-cols-1 md:grid-cols-10 gap-3"}
            [:div
             {:class "md:col-span-2 flex flex-col"}
             [:h3
              {:class "pb-4 text-xl font-bold"}
              (loc/tr :settings/account-settings)]
             [:div
              {:class "sticky top-0 flex md:flex-col gap-1 md:gap-4
                      text-base-light-text"}
              (->> (visible-tabs)
                   (map (fn [{:keys [key label route] :as _tab}]
                          ^{:key (str "settings-tab-" key)}
                          [link/side-nav-link {:active?  (= tab-key route)
                                               :dom-id   (str "account-navigation-" key)
                                               :on-click #(rf/dispatch [::event/side-nav-clicked [:account-settings :tab-key route]])}
                           label]))
                   (doall))]]
            [:div
             {:class "md:col-span-8 min-w-0 flex flex-col"}

             (cond (= tab-key state/config-route)
                   [configuration-card fundraising-plan?]
                   (= tab-key state/users-route)
                   [users-card]
                   (= tab-key state/guest-users-route)
                   [guest-users-card]
                   (= tab-key state/teams-route)
                   [teams-card]
                   (= tab-key state/insider-groups-route)
                   [insider-groups-card]
                   (= tab-key state/security-route)
                   [security-card]
                   (= tab-key state/zapai-route)
                   [zapai-card]
                   (= tab-key state/mcp-admin-route)
                   [mcp-admin-card]
                   (= tab-key state/modules-route)
                   [your-modules-card]
                   (= tab-key state/business-route)
                   [your-business-card]
                   (= tab-key state/data-route)
                   [your-data-card]
                   :else
                   (str "tab : " tab-key " " (type tab-key) " << "
                        (type state/config-route) " >> " state/config-route " >> "
                        (= tab-key state/config-route)))]]]]]))}))
(defmethod routing/render-view :account-settings
  [{:keys [route-params]}]
  (let [tab-key (or (:tab-key route-params)
                    state/config-route)]
    [default-page-layout/view
     [component tab-key]]))
